import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import { 
  Users, 
  Plus, 
  Mail, 
  Building2, 
  FileText,
  Trash2,
  Pencil,
  Share2,
  UsersRound,
  MessageSquare,
  MoreVertical,
  Link2,
  ExternalLink
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import QuickActions, { clientActions } from "@/components/ui/QuickActions";

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [sharingClient, setSharingClient] = useState(null);
  const [shareData, setShareData] = useState({ team_id: "", permissions: "view" });
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    notes: ""
  });

  const fetchClients = async () => {
    try {
      const res = await api.get("/clients");
      setClients(res.data);
    } catch (e) {
      console.error("Error fetching clients:", e);
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const res = await api.get("/teams");
      setTeams(res.data.teams || []);
    } catch (e) {
      console.error("Error fetching teams:", e);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchTeams();
  }, []);

  const resetForm = () => {
    setFormData({ name: "", email: "", company: "", notes: "" });
    setEditingClient(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const handleOpenEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || "",
      email: client.email || "",
      company: client.company || "",
      notes: client.notes || ""
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error("Client name is required");
      return;
    }

    try {
      if (editingClient) {
        // Update existing client
        await api.put(`/clients/${editingClient.id}`, formData);
        toast.success("Client updated successfully!");
      } else {
        // Create new client
        await api.post("/clients", formData);
        toast.success("Client created successfully!");
      }
      handleCloseForm();
      fetchClients();
    } catch (e) {
      console.error("Error saving client:", e);
      toast.error(editingClient ? "Failed to update client" : "Failed to create client");
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm("Are you sure? This will also delete all associated projects.")) {
      return;
    }

    try {
      await api.delete(`/clients/${clientId}`);
      toast.success("Client deleted");
      fetchClients();
    } catch (e) {
      console.error("Error deleting client:", e);
      toast.error("Failed to delete client");
    }
  };

  const handleOpenShare = (client) => {
    setSharingClient(client);
    setShareData({ team_id: teams[0]?.id || "", permissions: "view" });
    setShowShareDialog(true);
  };

  const handleShare = async () => {
    if (!shareData.team_id) {
      toast.error("Please select a team");
      return;
    }
    
    try {
      await api.post(`/teams/${shareData.team_id}/share`, {
        resource_type: "client",
        resource_id: sharingClient.id,
        permissions: shareData.permissions
      });
      toast.success(`Shared "${sharingClient.name}" with team`);
      setShowShareDialog(false);
      setSharingClient(null);
    } catch (e) {
      console.error("Error sharing client:", e);
      toast.error(e.response?.data?.detail || "Failed to share client");
    }
  };

  const handleGenerateReply = (client) => {
    // Navigate to Client Firewall with client context
    navigate("/firewall", { state: { clientId: client.id, clientName: client.name } });
  };

  const handleSendPortalInvite = async (client) => {
    if (!client.email) {
      toast.error("Client has no email address");
      return;
    }
    
    try {
      const res = await api.post("/client-portal/invite", { client_id: client.id });
      toast.success(`Portal invite sent to ${client.email}!`);
      
      // Also show the portal URL for manual sharing
      if (res.data.portal_url) {
        navigator.clipboard.writeText(res.data.portal_url);
        toast.info("Portal link copied to clipboard");
      }
    } catch (e) {
      console.error("Error sending portal invite:", e);
      toast.error(e.response?.data?.detail || "Failed to send portal invite");
    }
  };

  const getQuickActions = (client) => clientActions({
    onShare: () => handleOpenShare(client),
    onGenerateReply: () => handleGenerateReply(client),
    onEdit: () => handleOpenEdit(client),
    onDelete: () => handleDelete(client.id),
    onPortalInvite: () => handleSendPortalInvite(client)
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Clients</h1>
          <p className="text-slate-400">Manage your client relationships</p>
        </div>
        <button
          onClick={handleOpenCreate}
          className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-xl transition-colors"
          data-testid="add-client-btn"
        >
          <Plus className="w-5 h-5" />
          Add Client
        </button>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => !open && handleCloseForm()}>
        <DialogContent className="bg-slate-800 border-slate-700" aria-describedby="client-form-description">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingClient ? "Edit Client" : "Add New Client"}
            </DialogTitle>
            <p id="client-form-description" className="sr-only">
              {editingClient ? "Update client information" : "Fill in the form to add a new client"}
            </p>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-indigo-500 focus:outline-none"
                placeholder="Client name"
                data-testid="client-name-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-indigo-500 focus:outline-none"
                placeholder="client@example.com"
                data-testid="client-email-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Company</label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-indigo-500 focus:outline-none"
                placeholder="Company name"
                data-testid="client-company-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-indigo-500 focus:outline-none min-h-[80px]"
                placeholder="Any notes about this client..."
                data-testid="client-notes-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleCloseForm}
                className="flex-1 px-4 py-2 text-slate-400 hover:text-slate-200 border border-slate-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors"
                data-testid="submit-client-btn"
              >
                {editingClient ? "Save Changes" : "Add Client"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Clients Grid */}
      {clients.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((client) => (
            <Card key={client.id} className="bg-slate-800/50 border-slate-700/50 hover:border-slate-600 transition-colors">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-500/20 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{client.name}</h3>
                      {client.company && (
                        <p className="text-sm text-slate-400 flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {client.company}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <QuickActions 
                      actions={getQuickActions(client)}
                      triggerIcon={MoreVertical}
                      position="left"
                    />
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  {client.email && (
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                      <Mail className="w-4 h-4" />
                      {client.email}
                    </div>
                  )}
                  {client.notes && (
                    <div className="flex items-start gap-2 text-sm text-slate-400">
                      <FileText className="w-4 h-4 mt-0.5" />
                      <span className="line-clamp-2">{client.notes}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-slate-700">
                  <p className="text-xs text-slate-500">
                    Added {new Date(client.created_at).toLocaleDateString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-300 mb-2">No clients yet</h3>
            <p className="text-slate-500 mb-4">Add your first client to get started</p>
            <button
              onClick={handleOpenCreate}
              className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-xl transition-colors"
              data-testid="add-client-empty-btn"
            >
              <Plus className="w-5 h-5" />
              Add Client
            </button>
          </CardContent>
        </Card>
      )}

      {/* Share to Team Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-slate-800 border-slate-700" aria-describedby="share-dialog-description">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UsersRound className="w-5 h-5 text-green-400" />
              Share to Team
            </DialogTitle>
            <p id="share-dialog-description" className="sr-only">
              Share this client with your team members
            </p>
          </DialogHeader>
          
          {teams.length > 0 ? (
            <div className="space-y-4 mt-4">
              <p className="text-sm text-slate-400">
                Share <span className="text-white font-medium">{sharingClient?.name}</span> with:
              </p>
              
              <div>
                <label className="block text-sm text-slate-300 mb-1">Team</label>
                <Select value={shareData.team_id} onValueChange={(v) => setShareData({...shareData, team_id: v})}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white">
                    <SelectValue placeholder="Select a team" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {teams.map((team) => (
                      <SelectItem key={team.id} value={team.id} className="text-slate-300">
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm text-slate-300 mb-1">Permissions</label>
                <Select value={shareData.permissions} onValueChange={(v) => setShareData({...shareData, permissions: v})}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="view" className="text-slate-300">View only</SelectItem>
                    <SelectItem value="edit" className="text-slate-300">Can edit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowShareDialog(false)}
                  className="flex-1 px-4 py-2 text-slate-400 hover:text-slate-200 border border-slate-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleShare}
                  className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  data-testid="confirm-share-btn"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <UsersRound className="w-10 h-10 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">No teams yet</p>
              <p className="text-slate-500 text-xs mt-1">Create a team first to share clients</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
